import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AppService } from '../../services/app.service';
import { finalize } from 'rxjs/operators';
import { LessonObservationService } from '../../services/lesson-observation.service';
import { ILessonObservationEvent, ILessonObservationEventType, ILessonObservationSearch } from '../../models/LessonObservation';
import { ITableColumn } from '../../shared/table/table.component';
import { Utils } from '../../core/Utils';
import { NgForm, NgModel } from '@angular/forms';

interface IFilterOption {
    display: string;
    tagPrimary: string;
    tagSecondary: string;
}

interface ISubject extends IFilterOption {
    code: string;
    name: string;
}

interface ITeacher extends IFilterOption { }

interface ILocation extends IFilterOption { }

interface IRow extends ILessonObservationEvent {
    timeFromNum: number;
    timeToNum: number;
}

const cache: {
    types?: ILessonObservationEventType[],
    payload?: ILessonObservationSearch,
    events?: IRow[]
} = {};

@Component({
    selector: 'app-lesson-observation-event-select',
    templateUrl: './event-select.component.html'
})
export class LessonObservationEventSelectComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: LessonObservationService,
        private activeModal: NgbActiveModal
    ) { }

    types: ILessonObservationEventType[] = [];
    events: IRow[] = [];
    subjects: ISubject[] = [];
    teachers: ITeacher[] = [];
    locations: ILocation[] = [];

    filterSubject: ISubject;
    filterTeacher: ITeacher;
    filterLocation: ILocation;
    filterTimeFrom: string;
    filterTimeTo: string;

    isSubmitted: boolean;
    isLoading: boolean;
    isFetched: boolean;

    readonly columns: ITableColumn[] = [
        { property: 'SubjectCode', label: 'lessonObservation_lblSubject' },
        { property: 'Date', label: 'lessonObservation_lblDate', cssClass: 'hidden-md-down' },
        { property: 'Location', label: 'lessonObservation_lblLocation', cssClass: 'hidden-md-down' },
        { width: '1px' }
    ];

    readonly payload: ILessonObservationSearch = { Date: new Date() };
    readonly timePattern = '^([01]?[0-9]|2[0-3]):[0-5][0-9]$';

    readonly filterOptionFilterFn = (option: IFilterOption, term: string) => {
        const low = term.toLowerCase();
        return option.tagPrimary.includes(low) || (option.tagSecondary && option.tagSecondary.includes(low));
    };

    @ViewChild('form', { static: true }) private form: NgForm;
    @ViewChild('timeFromInput', { static: false }) private timeFromInput: NgModel;
    @ViewChild('timeToInput', { static: false }) private timeToInput: NgModel;

    private allEvents: IRow[] = [];

    ngOnInit() {
        if (cache.types) {
            this.types = cache.types;
        } else {
            this.app.addLoading(this.service.getEventTypes()).subscribe(data => {
                this.types = cache.types = data;
            });
        }

        if (cache.payload) {
            Object.assign(this.payload, cache.payload);
        }

        if (cache.events) {
            this.events = cache.events;
        }
    }

    search() {
        this.isSubmitted = true;

        if (!this.form.valid) return;

        this.isLoading = true;

        setTimeout(() => {
            this.service.getEvents(this.payload).pipe(finalize(() => {
                this.isLoading = false;
            })).subscribe(data => {
                this.allEvents = cache.events = data.map(t => {
                    return {
                        ...t,
                        timeFromNum: this.getTimeNumber(t.TimeFrom),
                        timeToNum: this.getTimeNumber(t.TimeTo)
                    };
                });

                const subjects: ISubject[] = [];
                const subjectKeys = new Set<string>();

                const teachers: ITeacher[] = [];
                const teacherKeys = new Set<string>();

                const locations: ILocation[] = [];
                const locationKeys = new Set<string>();

                data.forEach(t => {
                    if (t.SubjectCode || t.SubjectName) {
                        const subjTag = `${t.SubjectCode} ${t.SubjectName}`.toLowerCase();

                        if (!subjectKeys.has(subjTag)) {
                            subjectKeys.add(subjTag);

                            subjects.push({
                                code: t.SubjectCode,
                                name: t.SubjectName,
                                display: `[${t.SubjectCode}] ${t.SubjectName}`,
                                tagPrimary: subjTag,
                                tagSecondary: Utils.stripPunctuation(subjTag)
                            });
                        }
                    }

                    if (t.Location) {
                        const locationTag = t.Location.toLowerCase();

                        if (!locationKeys.has(locationTag)) {
                            locationKeys.add(locationTag);

                            locations.push({
                                display: t.Location,
                                tagPrimary: locationTag,
                                tagSecondary: Utils.stripPunctuation(locationTag)
                            });
                        }
                    }

                    t.Teachers.forEach(n => {
                        const teacherTag = `${n.Name} ${n.Surname}`.toLowerCase();

                        if (!teacherKeys.has(teacherTag)) {
                            teacherKeys.add(teacherTag);

                            teachers.push({
                                display: `${n.Name} ${n.Surname}`,
                                tagPrimary: teacherTag,
                                tagSecondary: Utils.stripPunctuation(teacherTag)
                            });
                        }
                    });
                });

                subjects.sort((a, b) => a.tagPrimary.localeCompare(b.tagPrimary, this.app.currentLanguage));
                teachers.sort((a, b) => a.tagPrimary.localeCompare(b.tagPrimary, this.app.currentLanguage));
                locations.sort((a, b) => a.tagPrimary.localeCompare(b.tagPrimary, this.app.currentLanguage));

                this.subjects = subjects;
                this.teachers = teachers;
                this.locations = locations;

                this.filterSubject = subjects.find(t => t.tagPrimary == this.filterSubject?.tagPrimary);
                this.filterTeacher = teachers.find(t => t.tagPrimary == this.filterTeacher?.tagPrimary);
                this.filterLocation = locations.find(t => t.tagPrimary == this.filterLocation?.tagPrimary);

                this.filter();

                this.isFetched = true;
            });
        }, 500);
    }

    filter() {
        const subj = this.filterSubject;
        const teacher = this.filterTeacher?.display;
        const location = this.filterLocation?.display;

        let timeFrom: number;
        let timeTo: number;

        if (this.filterTimeFrom && this.timeFromInput.valid) {
            timeFrom = this.getTimeNumber(this.filterTimeFrom);
        }

        if (this.filterTimeTo && this.timeToInput.valid) {
            timeTo = this.getTimeNumber(this.filterTimeTo);
        }

        if (subj || teacher || location || timeFrom || timeTo) {
            this.events = this.allEvents.filter(t => {
                if (timeFrom && t.timeFromNum < timeFrom) return false;
                if (timeTo && t.timeToNum < timeTo) return false;
                if (location && t.Location != location) return false;
                if (subj && (t.SubjectCode != this.filterSubject.code || t.SubjectName != this.filterSubject.name)) return false;
                if (teacher && !t.Teachers.some(n => `${n.Name} ${n.Surname}` != teacher)) return false;
                return true;
            });
        } else {
            this.events = this.allEvents;
        }
    }

    select(row: ILessonObservationEvent) {
        cache.payload = this.payload;
        this.activeModal.close(row);
    }

    cancel() {
        this.activeModal.dismiss();
    }

    private getTimeNumber(time: string) {
        if (!time) return 0;

        const parts = time.split(':');

        if (parts.length != 2) return 0;

        return Number(parts[0]) * 100 + Number(parts[1]);
    }
}
